import { ElLoading } from 'element-plus';
import type { LoadingInstance } from 'element-plus/es/components/loading/src/loading';
import { nextTick, ref } from 'vue';

export interface LoadingOptions {
    minDuration?: number;
    target?: HTMLElement | string;
    fullscreen?: boolean;
    background?: string;
    lock?: boolean;
}

interface ActiveLoading {
    id: string;
    instance: LoadingInstance | null;
    options: LoadingOptions;
    targetElement?: HTMLElement | null;
    createdAt: number;
}

const GLOBAL_LOADING_ID = '__global__';
const DEFAULT_MIN_DURATION = 300;
const DEFAULT_LOADING_OPTIONS: LoadingOptions = {
    fullscreen: true,
    lock: true,
    background: 'rgba(255, 255, 255, 0.8)',
};

const isLoading = ref(false);

const activeLoadings = new Map<string, ActiveLoading>();
let activeCount = 0;
let showStartTime = 0;
let currentMaxMinDuration = DEFAULT_MIN_DURATION;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let isReconstructing = false;

function mergeLoadingOptions(options: LoadingOptions = {}): LoadingOptions {
    return {
        ...DEFAULT_LOADING_OPTIONS,
        ...options,
    };
}

function getTargetElement(target?: HTMLElement | string): HTMLElement | undefined {
    if (!target) {
        return undefined;
    }
    if (target instanceof HTMLElement) {
        return target;
    }
    if (typeof target === 'string') {
        const el = document.querySelector(target);
        if (el instanceof HTMLElement) {
            return el;
        }
    }
    return undefined;
}

function createSingleLoadingInstance(options: LoadingOptions, targetElement?: HTMLElement): LoadingInstance {
    const mergedOptions: LoadingOptions = mergeLoadingOptions(options);
    const serviceOptions: Record<string, unknown> = {
        lock: mergedOptions.lock ?? true,
        background: mergedOptions.background ?? 'rgba(255, 255, 255, 0.8)',
        fullscreen: mergedOptions.fullscreen ?? true,
    };

    if (targetElement && !mergedOptions.fullscreen) {
        serviceOptions.target = targetElement;
        serviceOptions.fullscreen = false;
    }

    return ElLoading.service(serviceOptions as Parameters<typeof ElLoading.service>[0]);
}

function registerAndCreate(id: string, options: LoadingOptions): LoadingInstance {
    const targetElement = getTargetElement(options.target);
    const instance = createSingleLoadingInstance(options, targetElement);

    activeLoadings.set(id, {
        id,
        instance,
        options: mergeLoadingOptions(options),
        targetElement,
        createdAt: Date.now(),
    });

    return instance;
}

function ensureLoadingInstance(id: string, options: LoadingOptions) {
    const active = activeLoadings.get(id);
    if (active?.instance) {
        try {
            if (active.instance.$el?.isConnected !== false) {
                return;
            }
        } catch (error) {}
    }

    if (active?.instance) {
        try {
            active.instance.close();
        } catch (error) {}
        activeLoadings.delete(id);
    }

    registerAndCreate(id, options);
}

function reconstructAllActiveLoadings() {
    if (isReconstructing) {
        return;
    }

    isReconstructing = true;

    const loadingsToReconstruct: { id: string; options: LoadingOptions; targetElement?: HTMLElement | null }[] = [];

    for (const [id, active] of activeLoadings) {
        if (active.instance) {
            try {
                active.instance.close();
            } catch (error) {}
        }
        loadingsToReconstruct.push({
            id: active.id,
            options: active.options,
            targetElement: active.targetElement,
        });
    }

    activeLoadings.clear();

    for (const item of loadingsToReconstruct) {
        const newInstance = createSingleLoadingInstance(item.options, item.targetElement as HTMLElement);
        activeLoadings.set(item.id, {
            id: item.id,
            instance: newInstance,
            options: item.options,
            targetElement: item.targetElement,
            createdAt: Date.now(),
        });
    }

    if (activeLoadings.size > 0) {
        isLoading.value = true;
    }

    isReconstructing = false;
}

function clearAllInstances() {
    for (const [, active] of activeLoadings) {
        if (active.instance) {
            try {
                active.instance.close();
            } catch (error) {}
        }
    }
    activeLoadings.clear();
}

function clearHideTimer() {
    if (hideTimer !== null) {
        clearTimeout(hideTimer);
        hideTimer = null;
    }
}

function resetMinDuration() {
    currentMaxMinDuration = DEFAULT_MIN_DURATION;
}

function actuallyShow(minDuration: number, id: string, options: LoadingOptions) {
    const wasIdle = activeCount === 0;
    activeCount++;

    if (wasIdle) {
        clearHideTimer();
        showStartTime = Date.now();
        currentMaxMinDuration = minDuration;
        isLoading.value = true;
        ensureLoadingInstance(id, options);
        return;
    }

    currentMaxMinDuration = Math.max(currentMaxMinDuration, minDuration);
    ensureLoadingInstance(id, options);
}

function scheduleHide() {
    clearHideTimer();
    const elapsed = Date.now() - showStartTime;
    const remaining = Math.max(0, currentMaxMinDuration - elapsed);

    const doHide = () => {
        hideTimer = null;
        isLoading.value = false;
        clearAllInstances();
        resetMinDuration();
    };

    if (remaining > 0) {
        hideTimer = setTimeout(doHide, remaining);
    } else {
        doHide();
    }
}

function actuallyHide() {
    if (activeCount <= 0) {
        return;
    }

    activeCount--;

    if (activeCount === 0) {
        scheduleHide();
    }
}

function forceHide() {
    activeCount = 0;
    clearHideTimer();
    isLoading.value = false;
    clearAllInstances();
    resetMinDuration();
}

export async function resetLoadingOnLocaleChange() {
    await nextTick();
    if (activeLoadings.size > 0 || isLoading.value) {
        reconstructAllActiveLoadings();
    }
}

export function useLoading(options: LoadingOptions = {}) {
    const minDuration = options.minDuration ?? DEFAULT_MIN_DURATION;
    const loadingOptions = mergeLoadingOptions(options);
    const loadingId = options.target || options.fullscreen === false ? generateScopedLoadingId() : GLOBAL_LOADING_ID;

    const showLoading = () => {
        actuallyShow(minDuration, loadingId, loadingOptions);
    };

    const hideLoading = () => {
        actuallyHide();
    };

    const forceHideLoading = () => {
        forceHide();
    };

    const withLoading = async <T>(fn: () => Promise<T>): Promise<T> => {
        showLoading();
        try {
            const result = await fn();
            await nextTick();
            return result;
        } finally {
            hideLoading();
        }
    };

    return {
        isLoading,
        showLoading,
        hideLoading,
        forceHideLoading,
        withLoading,
    };
}

function generateScopedLoadingId() {
    return `loading_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * 延迟到当前调用栈结束、调用方完成数据赋值并触发 DOM 更新后再关闭 loading
 */
export function hideLoadingAfterRender() {
    setTimeout(async () => {
        await nextTick();
        hideLoading();
    }, 0);
}

const globalLoading = useLoading();

export const showLoading = globalLoading.showLoading;
export const hideLoading = globalLoading.hideLoading;
export const forceHideLoading = globalLoading.forceHideLoading;
export const withLoading = globalLoading.withLoading;

export { isLoading };
